import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { Button } from "@/components/ui/button";
import { breadcrumbSchema, faqSchema, jsonLd, absUrl } from "@/lib/seo";
import { GENERIC_BOKADIREKT, bookingUrlForSalon } from "@/lib/booking";
import { salons as allSalons } from "@/data/salons";
import { internalPath } from "@/lib/paths";

const BOOK_URL = GENERIC_BOKADIREKT;

const salons: { slug: string; name: string; address: string; hours: string; bookUrl: string }[] = [
  { slug: "taby", name: "Täby Centrum", address: "Biblioteksgången 1", hours: "mån–fre 10–20, lör 10–18, sön 10–18" },
  { slug: "ahlens-city", name: "Åhléns City", address: "Klarabergsgatan 50", hours: "mån–fre 10–20, lör 10–19, sön 11–19" },
  { slug: "akersberga", name: "Åkersberga ICA Maxi", address: "Långhundravägen 9", hours: "mån–fre 10–19, lör–sön 10–18" },
  { slug: "arninge", name: "Arninge ICA Maxi", address: "Kundvägen 1D", hours: "mån–fre 10–19, lör–sön 10–18" },
].map((s) => {
  const salon = allSalons.find((x) => x.slug === s.slug);
  return { ...s, bookUrl: salon ? bookingUrlForSalon(salon) : GENERIC_BOKADIREKT };
});

const faq = [
  { q: "Kan jag alltid komma på drop-in?", a: "I mån av tid. Vi rekommenderar att du ringer eller kollar online innan om du är osäker." },
  { q: "Hur lång är väntetiden vid drop-in?", a: "Varierar. Ibland direkt, ibland 15–30 min. Vi försöker alltid ta emot dig snabbt." },
  { q: "Kan jag boka online och komma direkt?", a: "Ja, onlinebokning via BokaDirekt är snabbt och enkelt." },
  { q: "Erbjuder ni drop-in för alla behandlingar?", a: "Ja, herrklippning, fade, skägg, barnklippning och övriga behandlingar." },
  { q: "Vilken salong är bäst för drop-in?", a: "Alla fyra salonger. Åhléns City är mest centralt. Täby och Arninge/Åkersberga har ofta god tillgänglighet under vardagar." },
];

export const Route = createFileRoute("/drop-in-barber-stockholm")({
  head: () => ({
    meta: [
      { title: "Drop-in barber Stockholm: 4 salonger utan bokning | Tailor's Barber" },
      { name: "description", content: "Drop-in barber i Stockholm hos Tailor's Barber. Kom förbi utan tidsbokning på fyra salonger. Herrklippning 450 kr, fade och skägg. Boka online för garanterad tid." },
      { property: "og:title", content: "Drop-in barber Stockholm: 4 salonger utan bokning | Tailor's Barber" },
      { property: "og:description", content: "Drop-in barber i Stockholm hos Tailor's Barber. Kom förbi utan tidsbokning på fyra salonger. Herrklippning 450 kr, fade och skägg. Boka online för garanterad tid." },
      { property: "og:url", content: absUrl("/drop-in-barber-stockholm") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: absUrl("/drop-in-barber-stockholm") }],
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Hem", url: "/" }, { name: "Drop-in", url: "/drop-in-barber-stockholm" }])),
      jsonLd(faqSchema(faq)),
    ],
  }),
  component: DropInPage,
});

function DropInPage() {
  return (
    <SiteShell>
      <section className="container-page pt-10 md:pt-16 pb-4">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Drop-in</span>
        </nav>
        <div className="eyebrow">Drop-in barber</div>
        <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
          Drop-in barber Stockholm: kom när du vill
        </h1>
        <p className="mt-5 text-lg text-foreground/80 max-w-prose leading-relaxed">
          Barber Stockholm drop in betyder att du kan komma förbi alla fyra salonger utan bokning i
          mån av tid. Vill du vara säker på en tid, boka online.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild size="lg">
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka online för garanterad tid</a>
          </Button>
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Vad är drop-in hos barberare?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Drop-in innebär att du kan komma till salongen utan att ha bokat en tid i förväg. Finns det en
          ledig barberare tas du emot direkt. Det passar perfekt för dig som har ett spontant behov eller
          inte vill planera i förväg.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Alla fyra salonger tar drop-in</h2>
        <p className="mt-4 text-foreground/80 max-w-3xl">
          Tailor&apos;s Barber erbjuder drop-in i mån av tid på alla fyra salonger i Stockholm.
          Söker du en <Link to={internalPath("/barberare-stockholm-ahlens-city/")} className="underline font-medium">Barber Stockholm</Link>{" "}
          i city eller en <Link to={internalPath("/barberare-stockholm-ahlens-city/")} className="underline font-medium">Barberare Stockholm City</Link>{" "}
          nära T-Centralen? Vårt drop-in på Åhléns City ligger på Klarabergsgatan 50, plan 3.
        </p>
        <ul className="mt-6 grid gap-3 md:grid-cols-2 max-w-3xl">
          {salons.map((s) => (
            <li key={s.name} className="card-elev p-5">
              <strong>{s.name}</strong>, {s.address}
              <div className="text-sm text-foreground/70 mt-1">Öppet {s.hours}</div>
              <a
                href={s.bookUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-cta="dropin_salon_card"
                className="mt-2 inline-block text-sm font-medium text-brass-deep hover:underline"
              >
                Boka i {s.name} på BokaDirekt →
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Bästa tiderna för drop-in</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Drop-in fungerar bäst: tidigt på morgonen precis när vi öppnar, vardagar mitt i veckan, på
          lunchen på vardagar, under kvällstimmarna på vardagar (efter 17). Drop-in kan vara mer
          begränsat: fredagar och lördagar, före helgdagar, på söndagar på Åhléns City. Vi rekommenderar
          alltid att boka online om du vill vara säker på en tid.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Drop-in eller onlinebokning?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Båda fungerar hos oss. Onlinebokning garanterar din tid och val av barberare. Drop-in ger
          flexibilitet utan att planera. Oavsett vilket möts du av samma höga kvalitet.
        </p>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Vill du komma förbi på helgen? Se tiderna för{" "}
          <Link to="/barberare-oppet-sondag-stockholm/" className="underline font-medium">
            barberare med öppet på söndagar i Stockholm
          </Link>
          .
        </p>
      </section>

      <Faq items={faq} heading="Vanliga frågor om drop-in" />

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Vill du vara säker på en tid?</h2>
          <p className="mt-3 text-foreground/80">Boka online så garanterar vi din tid och barberare.</p>
          <div className="mt-6">
            <Button asChild size="lg">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka online för garanterad tid</a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
